import React, { useRef, useState } from 'react';
import { Button, DatePicker, Input, Space } from 'antd';
import { CalendarOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// The filters read their own keys back as dates, which needs strict format parsing.
dayjs.extend(customParseFormat);

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';
/** A date-time as text that sorts: what a row's `sortKey` is compared with. */
const SORTABLE = 'YYYYMMDDHHmm';
/** A date as the server writes it (a business date), which sorts as text too. */
const DATE_KEY = 'YYYY-MM-DD';

/** The two range filters: to the minute against a sort key, or to the day against a business date. */
const RANGE = Object.freeze({
    dateTime: { key: SORTABLE, format: DATE_TIME_FORMAT, showTime: { format: 'HH:mm' } },
    date: { key: DATE_KEY, format: DATE_KEY, showTime: false },
});

const filterIcon = (Icon) => (filtered) => <Icon style={{ color: filtered ? 'var(--brand-primary)' : undefined }} />;

/** The text with every match of `term` marked. */
export const Highlight = ({ text, term }) => {
    const value = String(text ?? '');
    if (!term) return value;
    const pieces = [];
    const lower = value.toLowerCase();
    const needle = term.toLowerCase();
    let from = 0;
    for (let at = lower.indexOf(needle); at !== -1; at = lower.indexOf(needle, from)) {
        if (at > from) pieces.push(value.slice(from, at));
        pieces.push(<mark key={at} className="uploads-highlight">{value.slice(at, at + needle.length)}</mark>);
        from = at + needle.length;
    }
    pieces.push(value.slice(from));
    return pieces;
};

/** The dropdown's frame: its field, then Search and Reset. Enter in the field searches. */
const FilterPanel = ({ children, onSearch, onReset }) => (
    <div className="lfoc-styles-wrapper uploads-filter" onKeyDown={(e) => e.stopPropagation()}>
        {children}
        <Space>
            <Button type="primary" size="small" icon={<SearchOutlined />} onClick={onSearch}>Search</Button>
            <Button size="small" onClick={onReset}>Reset</Button>
        </Space>
    </div>
);

/** The filter's one key is `<from>|<to>`, each in its range's key format or empty. */
const RangeFilter = ({ selectedKeys, setSelectedKeys, confirm, clearFilters, range }) => {
    const [from, to] = (selectedKeys[0] ?? '|').split('|').map((key) => (key ? dayjs(key, range.key, true) : null));
    const set = (next) => setSelectedKeys(next.from || next.to
        ? [(next.from?.format(range.key) ?? '') + '|' + (next.to?.format(range.key) ?? '')]
        : []);
    return (
        <FilterPanel
            onSearch={() => confirm()}
            onReset={() => {
                clearFilters();
                confirm();
            }}
        >
            <DatePicker showTime={range.showTime} format={range.format} placeholder="From" aria-label="From"
                value={from} onChange={(picked) => set({ from: picked, to })} />
            <DatePicker showTime={range.showTime} format={range.format} placeholder="To" aria-label="To"
                value={to} onChange={(picked) => set({ from, to: picked })} />
        </FilterPanel>
    );
};

/**
 * Column search for a table: a text search whose matches are highlighted in
 * the searched column, a date-time range, and a date range.
 *
 *   const { textSearch, dateTimeRangeSearch } = useColumnSearch();
 *   { title: 'Message', dataIndex: 'message', ...textSearch('message', 'Message') }
 */
export function useColumnSearch() {
    const [searched, setSearched] = useState({});
    const input = useRef(null);

    /** `also` names further fields of the row the search covers; only `dataIndex` is highlighted. */
    const textSearch = (dataIndex, title, { also = [] } = {}) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
            const search = () => {
                confirm();
                setSearched((s) => ({ ...s, [dataIndex]: selectedKeys[0] ?? '' }));
            };
            const reset = () => {
                clearFilters();
                confirm();
                setSearched((s) => ({ ...s, [dataIndex]: '' }));
            };
            return (
                <FilterPanel onSearch={search} onReset={reset}>
                    <Input
                        ref={input}
                        placeholder={'Search ' + title}
                        aria-label={'Search ' + title}
                        value={selectedKeys[0] ?? ''}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={search}
                    />
                </FilterPanel>
            );
        },
        filterIcon: filterIcon(SearchOutlined),
        filterDropdownProps: { onOpenChange: (open) => open && setTimeout(() => input.current?.select(), 100) },
        onFilter: (value, row) => [dataIndex, ...also].some((field) => String(row[field] ?? '').toLowerCase().includes(String(value).toLowerCase())),
        render: (text) => <Highlight text={text} term={searched[dataIndex]} />
    });

    /** Inclusive at both ends, against the row's field in the range's key format. */
    const rangeSearch = (range, field) => ({
        filterDropdown: (props) => <RangeFilter {...props} range={range} />,
        filterIcon: filterIcon(CalendarOutlined),
        onFilter: (value, row) => {
            const [from, to] = String(value).split('|');
            const at = row[field];
            if (!at) return false;
            return (!from || at >= from) && (!to || at <= to);
        }
    });

    /** To the minute, against the row's `YYYYMMDDHHmm` key. */
    const dateTimeRangeSearch = (sortKeyIndex) => rangeSearch(RANGE.dateTime, sortKeyIndex);

    /** To the day, against a `YYYY-MM-DD` date (a business date): From and To the same day is that day. */
    const dateRangeSearch = (dateIndex) => rangeSearch(RANGE.date, dateIndex);

    return { textSearch, dateTimeRangeSearch, dateRangeSearch };
}
